import { PropsWithChildren, useMemo } from 'react';

import { DateRange, PriceType } from '@cast/shared-types';

import { useCostReportContext } from 'features/cost-report/hooks/useCostReportContext';
import {
  ComputeCostDynamicCopiesMap,
  CostOverTimeContext,
} from 'features/cost-report/providers/CostOverTimeProvider';
import { useWorkloadCostReportQuery } from 'hooks/queries/cost';

import { WorkloadDetailsProvider } from './WorkloadDetailsProvider';
import { transformWorkloadCostData, aggregateClusterMetrics } from '../utils';

type Props = {
  clusterId: string;
  workloadType: string;
  workloadName: string;
  namespace: string;
};

// prettier-ignore
const workloadCopiesMap: ComputeCostDynamicCopiesMap =  {
  summary: {
    totalSpendTooltip: 'Total workload compute spend for the selected period',
    currentSpendTooltip: 'Current month factual compute spend based on workload uptime',
    monthlyForecastTooltip: 'Takes into account factual workload uptime and forecast for the rest of the month. Forecast is calculated using 7 day moving average formula',
    monthlyAverageTooltip: 'Average monthly compute cost for selected time period. Calculated as daily average multiplied by 30 days',
    averageCpuCost: 'Average normalized cost per 1 requested CPU'
  },
  computeSpend: {
    mainTitle: 'Current day spend',
    mainTooltip: 'Current day factual compute spend based on workload uptime'
  },
  normalized: {
    mainDefaultTooltip: 'End of the day projected cost per normalized CPU',
    mainMonthlyTooltip: 'Monthly cost projection for 1 CPU, calculated as total hourly compute spend divided by the requested CPU and multiplied by 730',
    onDemandDefaultTooltip: 'End of the day projected cost per normalized on-demand CPU',
    onDemandMonthlyTooltip: 'Monthly cost projection for 1 on-demand CPU, calculated as total on-demand hourly compute spend divided by the requested on-demand CPU and multiplied by 730',
    fallbackDefaultTooltip: 'End of the day projected cost per normalized fallback CPU',
    fallbackMonthlyTooltip: 'Monthly cost projection for 1 fallback CPU, calculated as total fallback hourly compute spend divided by the requested fallback  CPU and multiplied by 730',
    spotDefaultTooltip: 'End of the day projected cost per normalized spot CPU',
    spotMonthlyTooltip: 'Monthly cost projection for 1 spot CPU, calculated as total spot hourly compute spend divided by the requested spot CPU and multiplied by 730',
  },
  podCost: {
    mainHourlyTooltip: 'Hourly workload cost divided by number of pods',
    mainDailyTooltip: 'Total daily workload cost divided by number of pods',
    mainMonthlyTooltip: 'Monthly cost projection for 1 pod, calculated as total hourly compute spend divided by the number of pods and multiplied by 730',
    onDemandHourlyTooltip: 'Hourly on-demand workload cost divided by number of pods',
    onDemandDailyTooltip: 'Total daily on-demand workload cost divided by the number of pods',
    onDemandMonthlyTooltip: 'Monthly cost projection for 1 on-demand pod, computed as average hourly cost per on-demand pod and multiplied by 730',
    fallbackHourlyTooltip: 'Hourly fallback workload cost divided by number of pods',
    fallbackDailyTooltip: 'Total daily fallback workload cost divided by the number of pods',
    fallbackMonthlyTooltip: 'Monthly cost projection for 1 fallback pod, computed as average hourly cost per fallback pod and multiplied by 730',
    spotHourlyTooltip: 'Hourly spot workload cost divided by number of pods',
    spotDailyTooltip: 'Total daily spot workload cost divided by the number of pods',
    spotMonthlyTooltip: 'Monthly cost projection for 1 spot pod, computed as average hourly cost per spot pod and multiplied by 730',
  },
  podCount: {
    average: 'Sum of daily pod count divided by total days in the period.'
  }
};

export const WorkloadCostOverTimeProvider = ({
  children,
  clusterId,
  workloadName,
  workloadType,
  namespace,
}: PropsWithChildren<Props>) => {
  const {
    isDefaultFilter,
    isCapabilitiesLoading,
    capabilitiesError,
    refetchCapabilities,
    timezone,
    adjustedDateRange,
    useListingPrices,
    includeIdleCosts,
  } = useCostReportContext();

  const previousRange: DateRange = useMemo(
    () => [
      adjustedDateRange[0].subtract(1, 'M'),
      adjustedDateRange[1].subtract(1, 'M'),
    ],
    [adjustedDateRange]
  );

  const {
    isLoading: isLoadingCurrentRange,
    workload: currentRangeData,
    error: currentError,
    refetch: refetchCurrent,
  } = useWorkloadCostReportQuery({
    clusterId,
    range: adjustedDateRange,
    workloadName,
    workloadType,
    namespace,
    useListingPrices,
    includeIdleCosts,
  });

  const {
    isLoading: isLoadingPreviousRange,
    workload: previousRangeData,
    error: previousError,
    refetch: refetchPrevious,
    noDataReason,
  } = useWorkloadCostReportQuery({
    clusterId,
    range: previousRange,
    workloadName,
    workloadType,
    namespace,
    useListingPrices,
    enabled: isDefaultFilter,
    includeIdleCosts,
  });

  const [currentUptimeData, currentUptimeChartData] = transformWorkloadCostData(
    adjustedDateRange,
    currentRangeData,
    timezone
  );

  const [previousUptimeData] = transformWorkloadCostData(
    previousRange,
    previousRangeData,
    timezone
  );

  const storageCostGranularity = PriceType.HOURLY;
  const aggregatedMetrics = useMemo(
    () =>
      aggregateClusterMetrics({
        isDefaultFilter,
        currentData: currentUptimeData,
        previousData: previousUptimeData,
        storageCostGranularity,
        timezone,
      }),
    [
      isDefaultFilter,
      currentUptimeData,
      previousUptimeData,
      storageCostGranularity,
      timezone,
    ]
  );

  const isLoading =
    isLoadingCurrentRange || isLoadingPreviousRange || isCapabilitiesLoading;
  const isCollecting = isDefaultFilter
    ? isLoading || (!currentUptimeData?.length && !previousUptimeData?.length)
    : isLoading;

  return (
    <CostOverTimeContext.Provider
      value={{
        key: 'workload',
        isCollecting,
        hasData: !!currentRangeData && !Object.keys(noDataReason || {}).length,
        error: currentError || previousError || capabilitiesError,
        projectEndOfDayCost: false,
        applyUptime: true,
        timeSeriesStep: PriceType.HOURLY,
        storageCostGranularity,
        refetch: () => {
          refetchCurrent();
          refetchPrevious();
          refetchCapabilities();
        },
        current: {
          isLoading: isLoadingCurrentRange || isCapabilitiesLoading,
          data: currentUptimeData,
          chartData: currentUptimeChartData,
          error: currentError,
        },
        previous: {
          isLoading: isLoadingPreviousRange,
          data: previousUptimeData,
          chartData: [],
          error: previousError,
        },
        aggregatedMetrics,
        noDataReason,
        dynamicCopiesMap: workloadCopiesMap,
      }}
    >
      <WorkloadDetailsProvider
        name={workloadName || ''}
        controller={workloadType || ''}
        namespace={namespace || ''}
      >
        {children}
      </WorkloadDetailsProvider>
    </CostOverTimeContext.Provider>
  );
};
